import React, { useState } from 'react';
import { format, subDays } from 'date-fns';
import { inventoryService, orderService } from '../../services/api';

const Reports = () => {
    const [selectedReport, setSelectedReport] = useState('inventory');
    const [dateRange, setDateRange] = useState({
                                                   startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
                                                   endDate: format(new Date(), 'yyyy-MM-dd')
                                               });
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
                                               category: '',
                                               status: '',
                                               supplier: '',
                                               customer: ''
                                           });

    const reportTypes = [
        { id: 'inventory', name: 'Inventory Status', description: 'Current inventory levels, stock values, and reorder suggestions' },
        { id: 'inventory-transactions', name: 'Inventory Transactions', description: 'History of all inventory movements, adjustments and transfers' },
        { id: 'production', name: 'Production Performance', description: 'Production batch completion rates, defects, and efficiency metrics' },
        { id: 'sales', name: 'Sales Analysis', description: 'Sales by product, customer, and time period with profitability analysis' },
        { id: 'purchases', name: 'Purchase History', description: 'Purchase orders by supplier, parts, and time period' },
        { id: 'cut-sheet', name: 'Cut Sheet', description: 'Generate cut sheet of parts in inventory' }
    ];

    const handleDateChange = (field, value) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Helper function to download data as a file
    const downloadAsFile = (data, filename, type = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') => {
        let blob;

        // Handle different response types
        if (data instanceof Blob) {
            blob = data;
        } else if (typeof data === 'string') {
            // For CSV or text data
            blob = new Blob([data], { type: type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ? 'text/csv' : type });
        } else if (data instanceof Response) {
            // Let the caller handle the Response object
            return data.blob().then(b => downloadAsFile(b, filename, type));
        } else {
            // For JSON or other data
            blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    const handleGenerateReport = async () => {
        setLoading(true);
        try {
            let response;
            const filename = `${selectedReport}-report-${format(new Date(), 'yyyy-MM-dd')}`;

            switch (selectedReport) {
                case 'inventory':
                    response = await inventoryService.exportInventoryReport({
                                                                                ...filters,
                                                                                ...dateRange
                                                                            });

                    if (response.data) {
                        // Handle if response is wrapped in a data property
                        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                        downloadFile(blob, `${filename}.xlsx`);
                    } else {
                        // Handle direct response
                        const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                        downloadFile(blob, `${filename}.xlsx`);
                    }
                    break;

                case 'inventory-transactions':
                    response = await inventoryService.exportTransactionsReport({
                                                                                   ...filters,
                                                                                   ...dateRange
                                                                               });

                    if (response.data) {
                        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                        downloadFile(blob, `${filename}.xlsx`);
                    } else {
                        const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                        downloadFile(blob, `${filename}.xlsx`);
                    }
                    break;

                case 'cut-sheet':
                    response = await inventoryService.generateCutSheet();

                    // Handle CSV specifically - extract the actual CSV data
                    if (response.data) {
                        const csvData = response.data;
                        const blob = new Blob([csvData], { type: 'text/csv' });
                        downloadFile(blob, `${filename}.csv`);
                    } else if (typeof response === 'string') {
                        // Direct CSV string
                        const blob = new Blob([response], { type: 'text/csv' });
                        downloadFile(blob, `${filename}.csv`);
                    } else {
                        // If it's the full response object we saw in the screenshot
                        // Check for content-type header to identify CSV
                        const contentType = response.headers &&
                                            response.headers['content-type'] &&
                                            response.headers['content-type'].includes('text/csv');

                        if (contentType) {
                            const blob = new Blob([response.data], { type: 'text/csv' });
                            downloadFile(blob, `${filename}.csv`);
                        } else {
                            // Try to extract CSV data from the response
                            console.log("Processing raw response:", response);
                            const csvData = extractCsvFromResponse(response);
                            const blob = new Blob([csvData], { type: 'text/csv' });
                            downloadFile(blob, `${filename}.csv`);
                        }
                    }
                    break;

                case 'production':
                case 'sales':
                case 'purchases':
                    // These endpoints use fetch and return Response objects
                    response = await fetch(`/api/reports/${selectedReport}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                                                 ...filters,
                                                 ...dateRange
                                             })
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const blob = await response.blob();
                    downloadFile(blob, `${filename}.xlsx`);
                    break;

                default:
                    throw new Error('Unknown report type');
            }
        } catch (error) {
            console.error("Error generating report:", error);
            alert("Failed to generate report. Please try again.");
        } finally {
            setLoading(false);
        }
    };

// Helper function to download a file
    const downloadFile = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

// Helper function to extract CSV data from various response formats
    const extractCsvFromResponse = (response) => {
        // Try to find CSV data in the response
        if (response.data && typeof response.data === 'string') {
            return response.data;
        }

        // If we have the response format from your screenshot, it's a JSON response
        // containing metadata but not the actual CSV data
        // We need to make another request to get the actual content

        // For now, return a message about the issue
        return "SKU,Part Type,Current Stock,Required for Orders,Threshold,Quantity to Order\n" +
               "Error: The API returned metadata instead of the actual CSV data.\n" +
               "Please check the API implementation.";
    };

    // Render different filter options based on the selected report
    const renderFilterOptions = () => {
        switch (selectedReport) {
            case 'inventory':
            case 'inventory-transactions':
                return (
                    <>
                        <div className="space-y-2">
                            <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                                Category
                            </label>
                            <select
                                id="category"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={filters.category}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                            >
                                <option value="">All Categories</option>
                                <option value="RAW">Raw Materials</option>
                                <option value="COMPONENT">Components</option>
                                <option value="PRODUCT">Finished Products</option>
                            </select>
                        </div>

                        {selectedReport === 'inventory-transactions' && (
                            <div className="space-y-2">
                                <label htmlFor="transactionType" className="block text-sm font-medium text-gray-700">
                                    Transaction Type
                                </label>
                                <select
                                    id="transactionType"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={filters.transactionType}
                                    onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                                >
                                    <option value="">All Types</option>
                                    <option value="RECEIVE">Receive</option>
                                    <option value="ISSUE">Issue</option>
                                    <option value="ADJUST">Adjustment</option>
                                    <option value="TRANSFER">Transfer</option>
                                </select>
                            </div>
                        )}
                    </>
                );
            case 'production':
                return (
                    <div className="space-y-2">
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                            Production Status
                        </label>
                        <select
                            id="status"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <option value="">All Statuses</option>
                            <option value="PLANNED">Planned</option>
                            <option value="IN_PROGRESS">In Progress</option>
                            <option value="COMPLETED">Completed</option>
                            <option value="CANCELLED">Cancelled</option>
                        </select>
                    </div>
                );
            case 'sales':
                return (
                    <>
                        <div className="space-y-2">
                            <label htmlFor="customer" className="block text-sm font-medium text-gray-700">
                                Customer
                            </label>
                            <select
                                id="customer"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={filters.customer}
                                onChange={(e) => handleFilterChange('customer', e.target.value)}
                            >
                                <option value="">All Customers</option>
                                {/* Customer options would be populated dynamically */}
                                <option value="1">Customer 1</option>
                                <option value="2">Customer 2</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="salesStatus" className="block text-sm font-medium text-gray-700">
                                Order Status
                            </label>
                            <select
                                id="salesStatus"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="">All Statuses</option>
                                <option value="CONFIRMED">Confirmed</option>
                                <option value="SHIPPED">Shipped</option>
                                <option value="DELIVERED">Delivered</option>
                                <option value="CANCELLED">Cancelled</option>
                            </select>
                        </div>
                    </>
                );
            case 'purchases':
                return (
                    <>
                        <div className="space-y-2">
                            <label htmlFor="supplier" className="block text-sm font-medium text-gray-700">
                                Supplier
                            </label>
                            <select
                                id="supplier"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={filters.supplier}
                                onChange={(e) => handleFilterChange('supplier', e.target.value)}
                            >
                                <option value="">All Suppliers</option>
                                {/* Supplier options would be populated dynamically */}
                                <option value="1">Supplier 1</option>
                                <option value="2">Supplier 2</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="purchaseStatus" className="block text-sm font-medium text-gray-700">
                                Order Status
                            </label>
                            <select
                                id="purchaseStatus"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="">All Statuses</option>
                                <option value="ORDERED">Ordered</option>
                                <option value="PARTIAL">Partially Received</option>
                                <option value="RECEIVED">Received</option>
                                <option value="CANCELLED">Cancelled</option>
                            </select>
                        </div>
                    </>
                );
            case 'cut-sheet':
                // No filters for cut sheet
                return null;
            default:
                return null;
        }
    };

    return (
        <div className="container mx-auto px-4 py-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Reports</h1>

            <div className="bg-white rounded-lg shadow mb-6">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">Generate Report</h2>
                </div>
                <div className="p-6">
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Report Type
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {reportTypes.map(report => (
                                <div
                                    key={report.id}
                                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                                        selectedReport === report.id
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                                    }`}
                                    onClick={() => setSelectedReport(report.id)}
                                >
                                    <h3 className="font-medium text-gray-900">{report.name}</h3>
                                    <p className="text-sm text-gray-500 mt-1">{report.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {selectedReport !== 'cut-sheet' && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date Range
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={dateRange.startDate}
                                        onChange={(e) => handleDateChange('startDate', e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        id="endDate"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={dateRange.endDate}
                                        onChange={(e) => handleDateChange('endDate', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {selectedReport !== 'cut-sheet' && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Filters
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {renderFilterOptions()}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end">
                        <button
                            onClick={handleGenerateReport}
                            disabled={loading}
                            className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                loading ? 'opacity-70 cursor-not-allowed' : ''
                            }`}
                        >
                            {loading ? (
                                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </span>
                            ) : (
                                 'Generate Report'
                             )}
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">Saved Reports</h2>
                </div>
                <div className="p-6">
                    <p className="text-gray-500 text-center py-8">
                        No saved reports found. Generate a report to save it for future reference.
                    </p>
                </div>
            </div>
        </div>
    );
};

// Export the new component with charts
import ReportsWithCharts from './ReportsWithCharts';
export default ReportsWithCharts;

// Keep the old Reports component available if needed
export { Reports as ReportsOld };
